// File: src/components/friends/ReportFlowSheet.jsx

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FaUserSecret,
  FaAngry,
  FaCameraRetro,
  FaExclamationTriangle,
  FaChild,
  FaBan,
} from 'react-icons/fa';
import { HiStatusOffline } from 'react-icons/hi';
import { IoArrowBackSharp, IoChevronForwardOutline } from 'react-icons/io5';
import { LuX } from 'react-icons/lu'; // match close icon used elsewhere
import { useToaster } from '../context/ToasterContext';
import { ReactComponent as TribeLogo } from '../assets/TribeLogo.svg';

function isPWA() {
  return (
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true
  );
}

const REPORT_OPTIONS = [
  'Fake Profile',
  'Harassment or Hate',
  'Inappropriate Photos',
  'Spam or Scam',
  'Underage User',
  'Offline behavior',
  'Remove User',
];

const reasonIcons = {
  'Fake Profile': <FaUserSecret style={{ marginRight: 8 }} />,
  'Harassment or Hate': <FaAngry style={{ marginRight: 8 }} />,
  'Inappropriate Photos': <FaCameraRetro style={{ marginRight: 8 }} />,
  'Spam or Scam': <FaExclamationTriangle style={{ marginRight: 8 }} />,
  'Underage User': <FaChild style={{ marginRight: 8 }} />,
  'Remove User': <FaBan style={{ marginRight: 8 }} />,
  'Offline behavior': <HiStatusOffline style={{ marginRight: 8 }} />,
};

export default function ReportFlowSheet({
  user,
  onClose,
  onDisconnectUsers,
  onBackToProfile,
  onExitAll,
  onUserBlocked,
}) {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedReason, setSelectedReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const { addToast } = useToaster();
  const scrollPositionRef = React.useRef(0);

  useEffect(() => {
    const themeMetaTag = document.querySelector('meta[name="theme-color"]');

    scrollPositionRef.current = window.scrollY || window.pageYOffset;
    document.body.style.top = `-${scrollPositionRef.current}px`;
    document.body.classList.add('overlay-active');
    document.documentElement.classList.add('overlay-active');
    document.getElementById('root').classList.add('overlay-active');
    if (themeMetaTag) themeMetaTag.content = '#999998';

    return () => {
      const scrollY = scrollPositionRef.current;
      document.body.classList.remove('overlay-active');
      document.documentElement.classList.remove('overlay-active');
      document.getElementById('root').classList.remove('overlay-active');
      document.body.style.top = '';
      window.scrollTo(0, scrollY);
      if (themeMetaTag) themeMetaTag.content = '#ffffff';
    };
  }, []);

  function handleBack() {
    if (currentStep === 1) {
      onBackToProfile?.();
    } else {
      setCurrentStep(1);
    }
  }

  function handleCloseAll() {
    onExitAll?.();
  }

  function handlePickReason(reason) {
    setSelectedReason(reason);
    if (reason === 'Remove User') {
      setCurrentStep(3);
    } else {
      setCurrentStep(2);
    }
  }

  async function handleSubmitDetails() {
    const firstName = user?.firstName || 'User';
    const lastInitial = user?.lastName ? user.lastName.charAt(0) + '.' : '';

    try {
      const resp = await axios.post('/report', {
        reporterId: localStorage.getItem('userId'),
        reportedUserId: user?._id,
        reason: selectedReason,
        details: reportDetails,
      });
      console.log('Report created:', resp.data);

      if (onUserBlocked) onUserBlocked(user?._id);
      addToast(`You have reported and removed ${firstName} ${lastInitial}.`, 'success');
    } catch (err) {
      console.error(err);
      addToast('Something went wrong reporting the user.', 'error');
    }

    onDisconnectUsers?.();
    onClose?.();
  }

  async function handleRemoveUser() {
    const firstName = user?.firstName || 'User';
    const lastInitial = user?.lastName ? user.lastName.charAt(0) + '.' : '';

    try {
      const resp = await axios.post('/block', {
        blockerId: localStorage.getItem('userId'),
        blockedId: user?._id,
      });
      console.log('User blocked =>', resp.data);
      if (onUserBlocked) onUserBlocked(user?._id);
      addToast(`You have blocked ${firstName} ${lastInitial}.`, 'success');
    } catch (err) {
      console.error(err);
      addToast('Something went wrong blocking this user.', 'error');
    }

    onDisconnectUsers?.();
    onClose?.();
  }

  if (currentStep === 2) {
    return (
      <Step2ReportText
        user={user}
        reason={selectedReason}
        reportDetails={reportDetails}
        onChangeDetails={(val) => setReportDetails(val)}
        onSubmit={handleSubmitDetails}
        onBack={() => setCurrentStep(1)}
      />
    );
  }

  return (
    <>
      <div style={styles.backdrop} onClick={handleCloseAll} />
      <div
        style={{
          ...styles.sheetContainer,
          marginBottom: isPWA() ? 40 : 8,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header bar (centered title, matching other pages) */}
        <div style={styles.headerBar}>
          <button
            aria-label="Back"
            style={styles.headerBackBtn}
            onClick={handleBack}
          >
<IoArrowBackSharp size={18} style={{ display: 'block' }} />
          </button>

          <div style={styles.headerTitle}>Report &amp; Remove</div>

          <button
            aria-label="Close"
            style={styles.headerCloseBtn}
            onClick={handleCloseAll}
          >
<LuX size={16} style={{ display: 'block' }} />
          </button>
        </div>

        {currentStep === 1 && <Step1SelectReason onSelect={handlePickReason} />}
        {currentStep === 3 && (
          <Step3RemoveUser
            user={user}
            reason={selectedReason}
            onConfirmRemove={handleRemoveUser}
          />
        )}
      </div>
    </>
  );
}

/** Step 1: "Select Reason" — rows with right chevron like Matches/Requests */
function Step1SelectReason({ onSelect }) {
  return (
    <div style={styles.stepContent}>
      <div style={styles.stepInnerContent}>
        {REPORT_OPTIONS.map((opt) => (
          <button
            key={opt}
            style={styles.listOption}
            onClick={() => onSelect(opt)}
          >
            <div style={styles.rowLeft}>
              {reasonIcons[opt]}
              <span>{opt}</span>
            </div>
            <IoChevronForwardOutline style={styles.rowChevron} />
          </button>
        ))}
      </div>
    </div>
  );
}

/** Step 2: Full-page "details" — updated header to match ProfileEditPage */
function Step2ReportText({
  user,
  reason,
  reportDetails,
  onChangeDetails,
  onSubmit,
  onBack,
}) {
  const [isFocused, setIsFocused] = useState(false);
  const scrollPositionRef = React.useRef(0);

  useEffect(() => {
    if (!isFocused) return;

    const originalOverflow = document.body.style.overflow;
    const preventTouchMove = (e) => e.preventDefault();

    window.scrollTo({ top: 0, behavior: 'instant' });

    const timeoutId = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }, 100);

    Object.assign(document.body.style, {
      position: 'fixed',
      top: '0px',
      left: '0',
      width: '100%',
      overflow: 'hidden',
    });

    document.addEventListener('touchmove', preventTouchMove, { passive: false });

    const handleResize = () => {
      window.scrollTo({ top: 0, behavior: 'instant' });
    };
    window.visualViewport?.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      window.visualViewport?.removeEventListener('resize', handleResize);
      document.removeEventListener('touchmove', preventTouchMove);

      Object.assign(document.body.style, {
        position: '',
        top: '',
        left: '',
        width: '',
        overflow: originalOverflow,
      });
    };
  }, [isFocused]);

  const MIN_REPORT_LENGTH = 25;
  const charLimit = 250;

  function getProfilePicUrl(u) {
    return u?.profilePicUrl || '/default-avatar.png';
  }

  const firstName = user?.firstName || 'User';
  const lastInitial = user?.lastName ? user.lastName.charAt(0) + '.' : '';
  const profilePicUrl = getProfilePicUrl(user);

  const isActive = reportDetails.trim().length >= MIN_REPORT_LENGTH;

  const typedSomething = reportDetails.trim().length > 0;
  const showRedBorder = typedSomething && reportDetails.trim().length < MIN_REPORT_LENGTH;
  const showMinCharMsg = typedSomething && reportDetails.trim().length < MIN_REPORT_LENGTH;

  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.content = '#ffffff';
    return () => {
      const metaExit = document.querySelector('meta[name="theme-color"]');
      if (metaExit) metaExit.content = '#999998';
    };
  }, []);

  function handleBackClick() {
    onBack?.();
  }
  function handleFocus() {
    setIsFocused(true);
  }
  function handleBlur() {
    setIsFocused(false);
  }

  return (
    <div style={pageStyles.pageContainer}>
      {/* Header aligned to ProfileEditPage */}
      <header style={pageStyles.headerContainer}>
        <div style={pageStyles.headerLogoRow}>
          <TribeLogo style={{ width: 72, height: 'auto' }} />
        </div>

        <div style={pageStyles.headerNavRowStyle}>
          <button onClick={handleBackClick} style={pageStyles.previousBackButtonStyle} aria-label="Back">
            <IoArrowBackSharp size={16} color="#fff" />
          </button>
          <div style={pageStyles.pageTitlePlainStyle}>Report &amp; Remove</div>
        </div>
      </header>

      <div style={pageStyles.mainContent}>
        <div
          style={{
            transition: 'opacity 0.3s ease, transform 0.3s ease, height 0.3s ease',
            opacity: isFocused ? 0 : 1,
            transform: isFocused ? 'translateY(-20px)' : 'translateY(0)',
            height: isFocused ? 0 : 'auto',
            overflow: 'hidden',
          }}
        >
          <img src={profilePicUrl} alt="User being reported" style={pageStyles.userPic} />
          <p style={pageStyles.subHeading}>
            Are you sure you want to report <strong>{firstName} {lastInitial}</strong> for <strong>{reason}</strong>?
          </p>
        </div>

        <div style={{ position: 'relative' }}>
          <textarea
            placeholder="Tell us what happened..."
            maxLength={charLimit}
            value={reportDetails}
            onChange={(e) => onChangeDetails(e.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            style={{
              ...pageStyles.textAreaTaller,
              transition: 'height 0.3s ease',
              height: isFocused ? '240px' : '300px',
              outline: 'none',
              overflowY: 'auto',
              borderColor: showRedBorder ? '#C22D2D' : '#ddd',
            }}
          />
          <div style={pageStyles.charCountInside}>
            {reportDetails.length}/{charLimit}
          </div>

          {showMinCharMsg && (
            <div
              style={{
                position: 'absolute',
                bottom: -20,
                left: 10,
                color: '#C22D2D',
                fontSize: '0.8rem',
              }}
            >
              Min. {MIN_REPORT_LENGTH} characters required.
            </div>
          )}
        </div>
      </div>

      {/* Fixed footer background + hairline (matches Edit Profile) */}
      {!isFocused && (
        <>
          <div
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              height: isPWA() ? '108px' : '72px',
              backgroundColor: '#fff',
              zIndex: 999,
              borderTop: '1px solid #eef0f2',
            }}
          />
          <div
            style={{
              ...pageStyles.bottomBar,
              zIndex: 1000,
              marginBottom: isPWA() ? 24 : 0,
              padding: '12px 16px',
              paddingBottom: isPWA() ? 'env(safe-area-inset-bottom)' : 12,
            }}
          >
            <button
              onClick={onSubmit}
              disabled={!isActive}
              style={getRedButtonStyle(isActive)}
            >
              Block & submit
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** Step 3 => remove user => calls /block */
function Step3RemoveUser({ user, reason, onConfirmRemove }) {
  const firstName = user?.firstName || 'User';
  const lastInitial = user?.lastName ? user.lastName.charAt(0) + '.' : '';
  const profilePicUrl = user?.profilePicUrl || '/default-avatar.png';

  // Friendly copy for the special "Remove User" case
  const displayReason = reason === 'Remove User' ? 'Other Reason' : reason;

  return (
    <div style={{ ...styles.stepContent, overflow: 'hidden' }}>
      <div style={{ ...styles.stepInnerContent, overflowY: 'auto' }}>
        <img src={profilePicUrl} alt="User to remove" style={styles.userPic} />
        <p style={styles.subHeading}>
          Are you sure you want to <strong>block</strong> {firstName} {lastInitial} for
          <strong> {displayReason}</strong>?
        </p>
        <p style={styles.removeText}>
          Removing this user will permanently delete all related information.
        </p>
      </div>

      <div style={styles.bottomBar}>
        <button onClick={onConfirmRemove} style={getRedButtonStyle(true)}>
          Block User
        </button>
      </div>
    </div>
  );
}

function getRedButtonStyle(isActive) {
  const activeBg = '#b91c1c';   // active red
  const inactiveBg = '#f87171'; // lighter red for inactive
  const inactiveBorder = '#fca5a5';

  return {
    width: '100%',
    maxWidth: 400,
    padding: '12px 0',
    fontSize: 12,
    fontWeight: 800,
    borderRadius: 10,
    border: `1px solid ${isActive ? activeBg : inactiveBorder}`,
    background: isActive ? activeBg : inactiveBg,
    color: '#fff',
    fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    margin: '0px auto',
    cursor: isActive ? 'pointer' : 'not-allowed',
  };
}

const styles = {
  sheetContainer: {
    position: 'fixed',
    left: 8,
    right: 8,
    bottom: 8,
    marginBottom: isPWA() ? 40 : 8,
    background: '#FFFFFF',
    border: '1px solid #E7E9EE',
    borderRadius: 12,
    animation: 'slideUp 0.25s ease-out',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 999,
    maxHeight: '70vh',
  },

  /* Header with centered title + matching buttons */
  headerBar: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 48,
    padding: '0 16px',
    borderBottom: '1px solid #eef0f2',
  },
  headerTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: '#0b0b0b',
    letterSpacing: '-0.01em',
    textAlign: 'center',
  },
  headerBackBtn: {
    position: 'absolute',
    left: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'transparent',
    border: 'none',
    width: 32,
    height: 32,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
    padding: 0,
    borderRadius: 8,
    color: '#0b0b0b',
    cursor: 'pointer',
  },
  headerCloseBtn: {
    position: 'absolute',
    right: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'transparent',
    border: '1px solid #e5e7eb',
    width: 32,
    height: 32,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
    padding: 0,
    borderRadius: 8,
    color: '#0b0b0b',
    cursor: 'pointer',
  },


  stepContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  stepInnerContent: {
    padding: 16,
    flex: 1,
  },

  /* Row style aligned with minimal look + chevron */
  listOption: {
    width: '100%',
    padding: '12px 14px',
    margin: '6px 0',
    borderRadius: 10,
    background: '#F7F8FA',
    border: '1px solid #E7E9EE',
    fontSize: 13,
    fontWeight: 600,
    color: '#0B1020',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    textAlign: 'left',
  },
  rowLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  rowChevron: {
    fontSize: 16,
    color: '#6B7280',
  },

  userPic: {
    width: 80,
    height: 80,
    borderRadius: '50%',
    objectFit: 'cover',
    display: 'block',
    margin: '0 auto 8px',
  },
  subHeading: {
    textAlign: 'center',
    fontSize: '0.95rem',
    margin: '8px auto 16px',
    lineHeight: 1.4,
    maxWidth: '280px',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
  },
  removeText: {
    margin: '0 0 16px',
    fontSize: '0.85rem',
    textAlign: 'center',
    color: '#666',
    lineHeight: 1.4,
  },
  bottomBar: {
    padding: '12px 16px',
    display: 'flex',
    justifyContent: 'center',
  },

  backdrop: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    zIndex: 998,
  },
};

const pageStyles = {
  pageContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 9999,
    overflow: 'hidden',
  },

  /* Header copied from ProfileEditPage (aligned) */
  headerContainer: {
    display: 'flex',
    flexDirection: 'column',
    position: 'sticky',
    top: 0,
    background: '#fff',
    zIndex: 100,
    fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },
  headerLogoRow: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '0.5rem',
  },
  headerNavRowStyle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.5rem 1rem',
    position: 'relative',
    height: 44,
  },
  previousBackButtonStyle: {
    position: 'absolute',
    left: 16,
    background: '#000',
    color: '#fff',
    borderRadius: 999,
    width: 40,
    height: 29,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    border: 'none',
  },
  pageTitlePlainStyle: {
    fontSize: '0.95rem',
    fontWeight: 900,
    lineHeight: 1,
    textTransform: 'uppercase',
    letterSpacing: 0.2,
  },
  bottomLine: {
    border: 'none',
    borderBottom: '1px solid #eee',
    margin: 0,
  },

  mainContent: {
    flex: 1,
    overflowY: 'auto',
    padding: '1rem 1rem 5rem',
    textAlign: 'center',
  },
  userPic: {
    width: 80,
    height: 80,
    borderRadius: '50%',
    objectFit: 'cover',
    display: 'block',
    margin: '0 auto 8px',
  },
  subHeading: {
    textAlign: 'center',
    fontSize: '0.95rem',
    margin: '8px auto 16px',
    lineHeight: 1.4,
    maxWidth: '280px',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
  },
  textAreaTaller: {
    width: '100%',
    border: '1px solid #ddd',
    borderRadius: 12,
    padding: 16,
    fontSize: 14,
    resize: 'none',
    fontFamily: "'Open Sans', sans-serif",
    margin: '0 auto',
    display: 'block',
    boxSizing: 'border-box',
  },
  charCountInside: {
    position: 'absolute',
    bottom: 12,
    right: 12,
    fontSize: 12,
    color: '#999',
  },
  bottomBar: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    padding: '1rem',
    backgroundColor: '#fff',
    textAlign: 'center',
  },
};
